import { Fade, Grid, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';
import React, { FunctionComponent, useContext, useEffect, useMemo, useState } from 'react';
import AnalyticsService from '../../../../shared/services/analytics/analytics.service';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { AnalyticMetric } from '../graphs/analytic-metric';
import { GraphLineChart } from '../graphs/graph-line-chart';
import { GraphBarChart } from '../graphs/graph-bar-chart';
import { getMixedColorPalette } from '../../../../shared/utils/colors-utils';
import { useSkeletonDelay } from '../../../../hooks/use-skeleton-delay';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import gaiaTheme from '../../../../root-components/theme';

type FlagDashboardPayload = {
  scopeTwoMethod: 'location' | 'market';
  summary: {
    flagEmissionsTco2e: number;
    flagRemovalsTco2e: number;
    netFlagTco2e: number;
    flagPctOfTotalEmissions: number;
    totalGrossEmissionsTco2e: number;
    nonFlagEmissionsTco2e: number;
  };
  trend: Array<{
    yearMonth: string;
    flagEmissionsTco2e: number;
    flagRemovalsTco2e: number;
    netFlagTco2e: number;
  }>;
  categoryBreakdown: Array<{ category: string; emissionsTco2e: number }>;
  removalsByCategory: Array<{ category: string; removalsTco2e: number }>;
  comparison: {
    flagEmissionsTco2e: number;
    nonFlagEmissionsTco2e: number;
    totalGrossEmissionsTco2e: number;
  };
  hotspots: Array<{
    emissionCalculationId: string;
    sourceLabel: string;
    flagCategory: string;
    dataTypeName: string;
    subsidiaryName: string;
    locationName: string;
    supplierName: string;
    flagEmissionsTco2e: number;
    flagRemovalsTco2e: number;
    netFlagTco2e: number;
  }>;
};

const FLAG_CONTRIBUTOR_HEAD_CELLS: IHeadCell[] = [
  { id: 'sourceLabel', numeric: false, label: 'Source', sortable: true, isHeading: true },
  { id: 'flagCategory', numeric: false, label: 'FLAG category', sortable: true, isSubHeading: true },
  { id: 'dataTypeName', numeric: false, label: 'Method', sortable: true },
  { id: 'flagEmissionsTco2e', numeric: true, label: 'Emissions (tCO2e)', sortable: true, align: 'right' },
  { id: 'flagRemovalsTco2e', numeric: true, label: 'Removals (tCO2e)', sortable: true, align: 'right' },
  { id: 'netFlagTco2e', numeric: true, label: 'Net (tCO2e)', sortable: true, align: 'right' },
  { id: 'subsidiaryName', numeric: false, label: 'Subsidiary', sortable: true },
  { id: 'locationName', numeric: false, label: 'Location', sortable: true },
  { id: 'supplierName', numeric: false, label: 'Supplier', sortable: true },
];

export const AnalyticsFlagTab: FunctionComponent<{
  reportPeriodId?: string;
  emissionType: 'marketBased' | 'locationBased';
}> = ({ reportPeriodId, emissionType }) => {
  const theme = useTheme();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('md'));
  const colors = getMixedColorPalette(theme.palette.primary.main);
  const [flagContributorSelected, setFlagContributorSelected] = useState<string[]>([]);
  const reportPeriodContext = useContext(ReportPeriodContext);
  const requiredReportPeriodId = reportPeriodId || reportPeriodContext?.lastSelectedReportId;
  const scopeTwoMethod = emissionType === 'marketBased' ? 'market' : 'location';

  const [data, setData] = useState<FlagDashboardPayload | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const showSkeleton = useSkeletonDelay(isLoading, 250);

  useEffect(() => {
    if (!requiredReportPeriodId) {
      setIsLoading(false);
      setData(null);
      return;
    }
    setIsLoading(true);
    setLoadError(null);
    const analyticsService = new AnalyticsService();
    analyticsService
      .getFlagDashboard(requiredReportPeriodId, scopeTwoMethod)
      .then((payload) => {
        setData(payload as FlagDashboardPayload);
      })
      .catch((err: { response?: { status?: number; data?: { message?: string } } }) => {
        const status = err?.response?.status;
        if (status === 403) {
          setLoadError('FLAG analytics is not available for your organisation.');
        } else if (status === 404) {
          setLoadError('Report period not found.');
        } else if (status === 401) {
          setLoadError('You do not have access to this report period.');
        } else {
          setLoadError('Unable to load FLAG analytics.');
        }
        setData(null);
      })
      .finally(() => setIsLoading(false));
  }, [requiredReportPeriodId, scopeTwoMethod]);

  const basisLabel = scopeTwoMethod === 'market' ? 'market' : 'location';

  const lineDataset =
    data?.trend?.map((t) => ({
      month: t.yearMonth,
      flagEmissionsTco2e: t.flagEmissionsTco2e,
      flagRemovalsTco2e: t.flagRemovalsTco2e,
      netFlagTco2e: t.netFlagTco2e,
    })) ?? [];

  const categoryBarDataset =
    data?.categoryBreakdown?.map((c) => ({
      label: c.category,
      totalEmission: c.emissionsTco2e,
    })) ?? [];

  const comparisonDataset = data
    ? [
        { label: 'FLAG emissions', totalEmission: data.comparison.flagEmissionsTco2e },
        { label: 'Non-FLAG emissions', totalEmission: data.comparison.nonFlagEmissionsTco2e },
        { label: 'Total gross emissions', totalEmission: data.comparison.totalGrossEmissionsTco2e },
      ]
    : [];

  const flagContributorRows = useMemo(
    () =>
      (data?.hotspots ?? []).map((row) => ({
        id: row.emissionCalculationId,
        sourceLabel: row.sourceLabel,
        flagCategory: row.flagCategory,
        dataTypeName: row.dataTypeName,
        flagEmissionsTco2e: Number(Number(row.flagEmissionsTco2e).toFixed(2)),
        flagRemovalsTco2e: Number(Number(row.flagRemovalsTco2e).toFixed(2)),
        netFlagTco2e: Number(Number(row.netFlagTco2e).toFixed(2)),
        subsidiaryName: row.subsidiaryName,
        locationName: row.locationName,
        supplierName: row.supplierName,
      })),
    [data?.hotspots],
  );

  return (
    <>
      {isLoading && (
        <Fade in={showSkeleton} unmountOnExit>
          <Typography variant="body2" color="text.secondary">
            Loading FLAG analytics…
          </Typography>
        </Fade>
      )}
      {!isLoading && loadError && (
        <Typography variant="body2" color="error">
          {loadError}
        </Typography>
      )}
      {!isLoading && !loadError && data && (
        <Fade in={!showSkeleton} unmountOnExit>
          <div>
            <Grid container spacing={2}>
              <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric
                  title="FLAG emissions"
                  tooltip={`Gross FLAG emissions for the reporting period (${basisLabel}-based). Removals excluded.`}
                  value={data.summary.flagEmissionsTco2e}
                  units="tCO2e"
                />
              </Grid>
              <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric
                  title="FLAG removals"
                  tooltip="Planned FLAG removals (tCO2e) from FLAG removal actions in the reduction plan."
                  value={data.summary.flagRemovalsTco2e}
                  units="tCO2e"
                />
              </Grid>
              <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric
                  title="Net FLAG position"
                  tooltip="FLAG emissions minus planned FLAG removals."
                  value={data.summary.netFlagTco2e}
                  units="tCO2e"
                />
              </Grid>
              <Grid item xl={3} lg={3} md={6} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <AnalyticMetric
                  title="FLAG % of total emissions"
                  tooltip="FLAG gross emissions divided by total gross emissions (same basis). Denominator includes all scopes; removals are excluded from both numerator and denominator."
                  value={data.summary.flagPctOfTotalEmissions}
                  units="%"
                />
              </Grid>

              <Grid item xl={12} lg={12} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                {lineDataset.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>
                      <b>FLAG performance over time</b>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      No monthly allocation breakdown is available for FLAG in this period (totals above still use full
                      calculation rows).
                    </Typography>
                  </Paper>
                ) : (
                  <GraphLineChart
                    title="FLAG performance over time"
                    tooltip={`Monthly series from allocation data, ${basisLabel}-based.`}
                    dataset={lineDataset}
                    dataKey="month"
                    colors={[colors[0], colors[2], colors[4]]}
                    denseTimeAxis={lineDataset.length > 5}
                    series={[
                      { dataKey: 'flagEmissionsTco2e', label: 'FLAG emissions' },
                      { dataKey: 'flagRemovalsTco2e', label: 'FLAG removals' },
                      { dataKey: 'netFlagTco2e', label: 'Net FLAG' },
                    ]}
                  />
                )}
              </Grid>

              <Grid item xl={4} lg={4} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <GraphBarChart
                  title="FLAG emissions by category"
                  tooltip="Gross FLAG emissions only (removals excluded)."
                  dataset={categoryBarDataset}
                  dataKey="label"
                  layout="horizontal"
                  colors={[colors[1]]}
                  series={[{ dataKey: 'totalEmission', label: 'tCO2e' }]}
                  chartHeight={Math.max(280, (categoryBarDataset.length || 1) * 52)}
                  horizontalLabelGutter={180}
                  paperHeight="auto"
                />
              </Grid>
              <Grid item xl={8} lg={8} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <GraphBarChart
                  title="FLAG vs non-FLAG vs total"
                  tooltip="Comparison on the same Scope 2 basis as the rest of this page."
                  dataset={comparisonDataset}
                  dataKey="label"
                  layout="horizontal"
                  colors={[colors[3]]}
                  series={[{ dataKey: 'totalEmission', label: 'tCO2e' }]}
                  chartHeight={Math.max(280, comparisonDataset.length * 76)}
                  horizontalLabelGutter={200}
                  paperHeight="auto"
                />
              </Grid>

              <Grid item xl={12} lg={12} md={12} sm={12} xs={12} sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography variant="subtitle1" sx={{ flex: 1, mb: 1 }}>
                  <b>Top FLAG contributors</b>
                </Typography>
                <TableDisplay
                  selectByIcon
                  isMobileMode={isMobileMode}
                  headCells={FLAG_CONTRIBUTOR_HEAD_CELLS}
                  rows={flagContributorRows}
                  selected={flagContributorSelected}
                  setSelected={setFlagContributorSelected}
                  perPage={10}
                />
              </Grid>
            </Grid>
          </div>
        </Fade>
      )}
    </>
  );
};
